import React, { useEffect, useState } from "react";
import axios from "axios";
import * as XLSX from 'xlsx';

const API_URL = "https://server.tradefxonline.com/admin_fetch_deposit_history.php";

const statusOptions = ["approved", "pending", "rejected"];

export default function DepositHistory() {
  const [deposits, setDeposits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("approved");
  const [imageModal, setImageModal] = useState({ isOpen: false, imageUrl: "", clientName: "" });

  const buildProofUrl = (proof) => {
    if (!proof || proof === "default_image.jpg") return "https://via.placeholder.com/600x400?text=No+Image";
    if (proof.startsWith("http")) return proof;
    if (proof.startsWith("/")) return `https://server.tradefxonline.com${proof}`;
    return `https://server.tradefxonline.com/uploads/${proof}`;
  };

  const exportToExcel = () => {
    if (deposits.length === 0) {
      alert('No data to export');
      return;
    }

    const exportData = deposits.map((d, idx) => ({
      '#': idx + 1,
      'Request ID': d.request_id,
      'Date & Time': d.created_at,
      'Email': d.email,
      'Trade ID': d.trade_id,
      'Amount': d.amount,
      'Payment Method': d.type === "0" ? "N/A" : d.type,
      'Status': d.status,
      'Admin Comment': d.admin_comment || '-',
      'Source': d.source || '-',
      'Deposit Address': d.deposit_address || '-'
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Deposit History');
    XLSX.writeFile(wb, `Deposit_History_${selectedStatus}_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const fetchDeposits = async (status) => {
    try {
      setLoading(true);
      setError("");
      const res = await axios.post(
        API_URL,
        { status },
        { headers: { "Content-Type": "application/json", Accept: "application/json" } }
      );
      if (res.data?.status === "success") {
        setDeposits(Array.isArray(res.data.data) ? res.data.data : []);
      } else {
        setError(res.data?.message || "Failed to fetch deposit history");
        setDeposits([]);
      }
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || "Failed to connect to server");
      setDeposits([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDeposits(selectedStatus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStatus]);

  const openImageModal = (imageUrl, clientName) => {
    const fullImageUrl = buildProofUrl(imageUrl);
    setImageModal({ isOpen: true, imageUrl: fullImageUrl, clientName });
  };

  const closeImageModal = () => setImageModal({ isOpen: false, imageUrl: "", clientName: "" });

  const StatusBadge = ({ status }) => {
    const key = String(status).toLowerCase();
    const label = key === "approved" ? "Approved" : key === "pending" ? "Pending" : key === "rejected" ? "Rejected" : String(status);
    return <span className={`status-badge status-${key}`}>{label}</span>;
  };

  return (
    <div className="main-content">
      <div className="page-header">
        <div>
          <h2>Deposit History</h2>
          <p className="subtitle">View and filter deposit requests by status</p>
        </div>
        <div className="header-actions">
          <div className="status-toggle" role="tablist" aria-label="Filter by status">
            {statusOptions.map((opt) => (
              <button
                key={opt}
                role="tab"
                aria-selected={selectedStatus === opt}
                className={`status-option ${opt} ${selectedStatus === opt ? "active" : ""}`}
                onClick={() => setSelectedStatus(opt)}
                disabled={loading}
              >
                {opt === "approved" ? "Approved" : opt === "pending" ? "Pending" : "Rejected"}
              </button>
            ))}
          </div>
          <button className="btn blue" onClick={() => fetchDeposits(selectedStatus)} disabled={loading}>
            {loading ? "Refreshing..." : "Refresh"}
          </button>
          <button className="btn green" onClick={exportToExcel} disabled={loading || deposits.length === 0}>
            Export to Excel
          </button>
        </div>
      </div>

      <div className="card">
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Request ID</th>
                <th>Date & Time</th>
                <th>Email</th>
                <th>Trade ID</th>
                <th>Amount</th>
                <th>Payment Method</th>
                <th>Status</th>
                <th>Proof</th>
                <th>Admin Comment</th>
                <th>Source</th>
                <th>Deposit Address</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="12" style={{ textAlign: "center" }}>Loading...</td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan="12" style={{ color: "crimson", textAlign: "center" }}>{error}</td>
                </tr>
              ) : deposits.length === 0 ? (
                <tr>
                  <td colSpan="12" style={{ textAlign: "center" }}>No records found</td>
                </tr>
              ) : (
                deposits.map((d, idx) => (
                  <tr key={`${d.request_id}-${idx}`}>
                    <td>{idx + 1}</td>
                    <td>{d.request_id}</td>
                    <td>{d.created_at}</td>
                    <td>{d.email}</td>
                    <td>{d.trade_id}</td>
                    <td>{d.amount}</td>
                    <td>{d.type === "0" ? "N/A" : d.type}</td>
                    <td><StatusBadge status={d.status} /></td>
                    <td>
                      {d.transaction_proof ? (
                        <button className="view-proof-btn" onClick={() => openImageModal(d.transaction_proof, d.email)} title="View Transaction Proof">
                          View Image
                        </button>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td>{d.admin_comment || "-"}</td>
                    <td>{d.source || "-"}</td>
                    <td>
                      {d.deposit_address ? (
                        <span
                          className="copyable-address"
                          title="Click to copy"
                          style={{ cursor: 'pointer', color: '#2563eb', textDecoration: 'underline' }}
                          onClick={() => {
                            navigator.clipboard.writeText(d.deposit_address);
                          }}
                          onMouseOver={e => e.currentTarget.style.backgroundColor = '#e0e7ff'}
                          onMouseOut={e => e.currentTarget.style.backgroundColor = ''}
                        >
                          {d.deposit_address.length > 20 ? `${d.deposit_address.substring(0, 20)}...` : d.deposit_address}
                        </span>
                      ) : (
                        '-'
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {imageModal.isOpen && (
        <div className="image-modal-overlay" onClick={closeImageModal}>
          <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="image-modal-header">
              <h3>Transaction Proof - {imageModal.clientName}</h3>
              <button className="image-modal-close" onClick={closeImageModal}>×</button>
            </div>
            <div className="image-modal-body">
              <img
                src={imageModal.imageUrl}
                alt="Transaction Proof"
                className="proof-image"
                onError={(e) => {
                  e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjBmMGYwIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkltYWdlIG5vdCBmb3VuZDwvdGV4dD48L3N2Zz4=';
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
